<script setup>
import { ref, onUnmounted } from 'vue';
import axios from 'axios';
import { ElNotification } from 'element-plus';
import Pica from 'pica';

// 用户信息
const userInfo = ref(null);

// 根据环境设置API基础URL
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5014' 
  : 'https://creatinf.com';

/**
 * 更新本地用户配额信息
 */
function updateUserQuotaInfo(quotaInfo) {
  if (quotaInfo) {
    userInfo.value = {
      ...userInfo.value,
      daily_free_images: quotaInfo.daily_free_images,
      remaining_images: quotaInfo.remaining_images
    };
    localStorage.setItem('userInfo', JSON.stringify(userInfo.value));
  }
}

// 用户输入的提示词
const userPrompt = ref('');
// 加载状态，用于禁用输入和显示提示
const isLoading = ref(false);
// 错误信息显示
const error = ref(null);
// 上传的图片文件
const selectedFile = ref(null);
// 上传图片的预览URL
const previewUrl = ref('');
// 生成的图片URL
const generatedImageUrl = ref('');
// 生成的图片blob数据
const generatedImageBlob = ref(null);
// 记录已扣费的图片hash
const paidImageHash = ref('');
// 文件输入框的DOM引用
const fileInput = ref(null);
// 拖拽状态
const isDragging = ref(false);
// 倒计时相关
const countdown = ref(0);
const isCountdownActive = ref(false);
const countdownInterval = ref(null);
// 生成进度相关
const generationProgress = ref(0);
const progressMessage = ref('');
// 队列相关
const isQueued = ref(false);
const queuePosition = ref(0);
const queueInterval = ref(null);
// 教程显示状态
const showTutorial = ref(false);
// 画框功能相关
const canvasRef = ref(null);
const isDrawing = ref(false);
const drawingMode = ref(false);
const selectedColor = ref('#ff0000');
const lineWidth = ref(3);
const boxes = ref([]);
const currentBox = ref(null);
const imageRef = ref(null);
// 图像在canvas中的实际显示区域
const imageDisplayArea = ref({ x: 0, y: 0, width: 0, height: 0 });

/**
 * 将canvas上的框坐标映射到原图分辨率
 */
function mapBoxToOriginalImage(box) {
  if (!imageRef.value) return null;
  const img = imageRef.value;
  const area = imageDisplayArea.value;
  // 计算缩放比例
  const scaleX = img.naturalWidth / area.width;
  const scaleY = img.naturalHeight / area.height;
  return {
    x: box.x * scaleX,
    y: box.y * scaleY,
    width: box.width * scaleX,
    height: box.height * scaleY,
    color: box.color,
    lineWidth: box.lineWidth * ((scaleX + scaleY) / 2) // 线宽也按比例缩放
  };
}

/**
 * 开始倒计时,倒计时40秒钟
 */
const startCountdown = () => {
  countdown.value = 16; // 预计等待时间改为16秒
  isCountdownActive.value = true;
  countdownInterval.value = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(countdownInterval.value);
      isCountdownActive.value = false;
    }
  }, 1000);
};

/**
 * 停止倒计时
 */
const stopCountdown = () => {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
    countdownInterval.value = null;
  }
  isCountdownActive.value = false;
  countdown.value = 0;
};

/**
 * 更新队列倒计时
 */
const updateQueueCountdown = (estimatedSeconds) => {
  countdown.value = estimatedSeconds;
  isCountdownActive.value = true;
  
  // 清除之前的倒计时
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
  }
  
  // 启动新的倒计时
  countdownInterval.value = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(countdownInterval.value);
      isCountdownActive.value = false;
    }
  }, 1000);
};

/**
 * 开始队列轮询
 */
const startQueuePolling = (taskId) => {
  queueInterval.value = setInterval(async () => {
    try {
      // 首先查询任务状态
      const resultResponse = await axios.get(`${API_BASE_URL}/api/i2i/task/${taskId}`);
      const resultData = resultResponse.data;
      
      if (resultData.status === 'processing') {
        // 任务正在处理中
        isQueued.value = false;
        isLoading.value = true;
        progressMessage.value = resultData.message || '任务正在处理中...';
        console.log('任务状态: 正在处理中');
      } else if (resultData.status === 'completed' && resultData.success) {
        // 任务成功完成
        const originalUrl = `${API_BASE_URL}/api/i2i/image/${resultData.image_filename}`;
        
        // 获取图片blob数据
        try {
          const imageResponse = await fetch(originalUrl);
          generatedImageBlob.value = await imageResponse.blob();
          // 创建blob URL用于展示
          generatedImageUrl.value = URL.createObjectURL(generatedImageBlob.value);
        } catch (err) {
          console.error('获取图片blob失败:', err);
          // 如果获取失败，使用原始URL作为备用
          generatedImageUrl.value = originalUrl;
        }
        
        generationProgress.value = 100;
        console.log('翻译后的提示词:', resultData.translated_prompt);
        
        // 显示处理成功通知
        ElNotification({
          title: '处理成功',
          message: '图片已成功生成，可以下载或继续编辑',
          type: 'success',
          duration: 3000,
          position: 'top-right'
        });
        
        isLoading.value = false;
        stopCountdown();
        stopQueuePolling();
        isQueued.value = false;
        queuePosition.value = 0;
        progressMessage.value = '';
      } else if (resultData.status === 'failed' || resultData.status === 'timeout') {
        // 任务失败或超时
        const errorMessage = resultData.error || '处理失败';
        error.value = errorMessage;
        
        // 使用Element Plus通知显示错误
        ElNotification({
          title: '处理失败',
          message: errorMessage,
          type: 'error',
          duration: 5000,
          position: 'top-right'
        });
        
        isLoading.value = false;
        stopCountdown();
        stopQueuePolling();
        isQueued.value = false;
        queuePosition.value = 0;
        progressMessage.value = '';
      } else if (resultData.status === 'queued') {
        // 任务在队列中，更新队列信息
        isQueued.value = true;
        isLoading.value = false;
        queuePosition.value = resultData.queue_position || 1;
        
        // 更新倒计时信息
        if (resultData.estimated_wait_time) {
          // 如果有预估等待时间，更新倒计时
          const estimatedSeconds = resultData.estimated_wait_time;
          if (countdown.value !== estimatedSeconds) {
            updateQueueCountdown(estimatedSeconds);
            console.log(`队列位置更新: 第${queuePosition.value}位，预估等待${estimatedSeconds}秒`);
          }
        }
        
        // 检查队列更新时间戳，判断是否需要刷新显示
        if (resultData.queue_update_time) {
          console.log('队列状态已更新:', {
            position: queuePosition.value,
            estimated_wait_time: resultData.estimated_wait_time,
            update_time: resultData.queue_update_time
          });
        }
      }
      
    } catch (resultErr) {
      if (resultErr.response && resultErr.response.status === 404) {
        // 任务结果还不存在，查询队列状态
        try {
          const queueResponse = await axios.get(`${API_BASE_URL}/api/i2i/queue/status`);
          const queueData = queueResponse.data;
          
          // 检查队列中是否还有我们的任务
          const ourTask = queueData.queue.find(task => task.task_id === taskId);
          if (ourTask) {
            // 更新排队位置
            const position = queueData.queue.findIndex(task => task.task_id === taskId) + 1;
            queuePosition.value = position;
            isQueued.value = true;
            isLoading.value = false;
            
            // 更新倒计时
            if (ourTask.estimated_wait_time) {
              updateQueueCountdown(ourTask.estimated_wait_time);
            }
          } else if (queueData.current_task && queueData.current_task.task_id === taskId) {
            // 任务正在处理中
            isQueued.value = false;
            isLoading.value = true;
            progressMessage.value = queueData.current_task.message || '任务正在处理中...';
          }
        } catch (queueErr) {
          console.error('查询队列状态时发生错误:', queueErr);
          // 使用Element Plus通知显示错误
          ElNotification({
            title: '查询失败',
            message: '查询队列状态时发生错误，请稍后重试',
            type: 'warning',
            duration: 3000,
            position: 'top-right'
          });
        }
      } else {
        console.error('查询任务结果时发生错误:', resultErr);
        // 使用Element Plus通知显示错误
        ElNotification({
          title: '查询失败',
          message: '查询任务结果时发生错误，请稍后重试',
          type: 'warning',
          duration: 3000,
          position: 'top-right'
        });
      }
    }
  }, 2000); // 每2秒查询一次，提高响应速度
};

/**
 * 停止队列轮询
 */
const stopQueuePolling = () => {
  if (queueInterval.value) {
    clearInterval(queueInterval.value);
    queueInterval.value = null;
  }
};

/**
 * 组件卸载时清理倒计时和队列轮询
 */
onUnmounted(() => {
  stopCountdown();
  stopQueuePolling();
  
  // 清理blob URL
  if (generatedImageUrl.value && generatedImageUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(generatedImageUrl.value);
  }
});

// 监听窗口大小变化，重新设置canvas
const handleResize = () => {
  if (previewUrl.value && canvasRef.value) {
    // 延迟执行，确保DOM更新完成
    setTimeout(() => {
      setupCanvas();
    }, 100);
  }
};

// 添加窗口大小变化监听
window.addEventListener('resize', handleResize);

// 组件卸载时移除监听器
onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

/**
 * 画框功能相关方法
 */
const toggleDrawingMode = () => {
  drawingMode.value = !drawingMode.value;
  if (!drawingMode.value) {
    isDrawing.value = false;
    currentBox.value = null;
  }
};

const getCanvasCoordinates = (event) => {
  const canvas = canvasRef.value;
  const rect = canvas.getBoundingClientRect();
  
  // 获取鼠标或触摸位置
  const clientX = event.clientX || event.touches?.[0]?.clientX;
  const clientY = event.clientY || event.touches?.[0]?.clientY;
  
  // 计算相对于画布的位置（现在canvas像素尺寸与CSS尺寸一致，无需缩放）
  const canvasX = clientX - rect.left;
  const canvasY = clientY - rect.top;
  
  // 获取图像实际显示区域
  const imageArea = imageDisplayArea.value;
  
  // 检查坐标是否在图像显示区域内
  if (canvasX < imageArea.x || canvasX > imageArea.x + imageArea.width ||
      canvasY < imageArea.y || canvasY > imageArea.y + imageArea.height) {
    // 坐标在图像区域外，返回null表示无效坐标
    return null;
  }
  
  // 将坐标转换为相对于图像显示区域的坐标
  const result = {
    x: canvasX - imageArea.x,
    y: canvasY - imageArea.y
  };
  
  // 调试坐标转换
  console.log('坐标转换:', {
    client: { x: clientX, y: clientY },
    rect: { left: rect.left, top: rect.top },
    canvasCoords: { x: canvasX, y: canvasY },
    imageArea: imageArea,
    result: result
  });
  
  return result;
};

const startDrawing = (event) => {
  if (!drawingMode.value) return;
  
  const coords = getCanvasCoordinates(event);
  if (!coords) return; // 如果坐标无效，不开始绘制
  
  isDrawing.value = true;
  currentBox.value = {
    x: coords.x,
    y: coords.y,
    width: 0,
    height: 0,
    color: selectedColor.value,
    lineWidth: lineWidth.value
  };
  
  // 调试信息
  console.log('开始绘制:', {
    clientX: event.clientX || event.touches?.[0]?.clientX,
    clientY: event.clientY || event.touches?.[0]?.clientY,
    canvasCoords: coords,
    canvasSize: { width: canvasRef.value.width, height: canvasRef.value.height },
    displaySize: { width: canvasRef.value.getBoundingClientRect().width, height: canvasRef.value.getBoundingClientRect().height }
  });
};

const draw = (event) => {
  if (!isDrawing.value || !currentBox.value) return;
  
  const coords = getCanvasCoordinates(event);
  if (!coords) return; // 如果坐标无效，不更新绘制
  
  currentBox.value.width = coords.x - currentBox.value.x;
  currentBox.value.height = coords.y - currentBox.value.y;
  
  redrawCanvas();
};

const stopDrawing = () => {
  if (!isDrawing.value || !currentBox.value) return;
  
  // 确保框有最小尺寸
  if (Math.abs(currentBox.value.width) < 10 || Math.abs(currentBox.value.height) < 10) {
    currentBox.value = null;
    isDrawing.value = false;
    redrawCanvas();
    return;
  }
  
  // 标准化坐标（确保width和height为正数）
  if (currentBox.value.width < 0) {
    currentBox.value.x += currentBox.value.width;
    currentBox.value.width = Math.abs(currentBox.value.width);
  }
  if (currentBox.value.height < 0) {
    currentBox.value.y += currentBox.value.height;
    currentBox.value.height = Math.abs(currentBox.value.height);
  }
  
  boxes.value.push({ ...currentBox.value });
  currentBox.value = null;
  isDrawing.value = false;
  redrawCanvas();
};

const redrawCanvas = () => {
  const canvas = canvasRef.value;
  const ctx = canvas.getContext('2d');
  const imageArea = imageDisplayArea.value;
  
  // 清除画布
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
  // 重新绘制图像
  if (imageRef.value && imageRef.value.complete) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(imageRef.value, imageArea.x, imageArea.y, imageArea.width, imageArea.height);
  }
  
  // 绘制所有已保存的框（现在坐标已经是相对于图像区域的）
  boxes.value.forEach(box => {
    ctx.strokeStyle = box.color;
    ctx.lineWidth = box.lineWidth;
    ctx.lineCap = 'square';
    ctx.lineJoin = 'miter';
    ctx.strokeRect(box.x + imageArea.x, box.y + imageArea.y, box.width, box.height);
  });
  
  // 绘制当前正在绘制的框（现在坐标已经是相对于图像区域的）
  if (currentBox.value) {
    ctx.strokeStyle = currentBox.value.color;
    ctx.lineWidth = currentBox.value.lineWidth;
    ctx.lineCap = 'square';
    ctx.lineJoin = 'miter';
    ctx.strokeRect(currentBox.value.x + imageArea.x, currentBox.value.y + imageArea.y, currentBox.value.width, currentBox.value.height);
  }
};

const clearBoxes = () => {
  boxes.value = [];
  currentBox.value = null;
  redrawCanvas();
};

const removeLastBox = () => {
  if (boxes.value.length > 0) {
    boxes.value.pop();
    redrawCanvas();
  }
};

const setupCanvas = () => {
  if (!canvasRef.value || !imageRef.value) return;
  
  const canvas = canvasRef.value;
  const img = imageRef.value;
  
  // 等待图像完全加载
  if (!img.complete) {
    img.onload = () => setupCanvas();
    return;
  }
  
  // 获取canvas的CSS显示尺寸
  const rect = canvas.getBoundingClientRect();
  const cssWidth = rect.width;
  const cssHeight = rect.height;
  
  // 确保canvas有有效的尺寸
  if (cssWidth <= 0 || cssHeight <= 0) {
    console.warn('Canvas尺寸无效，延迟重试');
    setTimeout(() => setupCanvas(), 100);
    return;
  }
  
  // 设置canvas的像素尺寸为CSS显示尺寸
  canvas.width = cssWidth;
  canvas.height = cssHeight;
  
  // 计算图像在canvas中的实际显示区域（保持宽高比）
  const canvasAspectRatio = cssWidth / cssHeight;
  const imageAspectRatio = img.naturalWidth / img.naturalHeight;
  
  let displayWidth, displayHeight, offsetX, offsetY;
  
  if (imageAspectRatio > canvasAspectRatio) {
    // 图像更宽，以宽度为准
    displayWidth = cssWidth;
    displayHeight = cssWidth / imageAspectRatio;
    offsetX = 0;
    offsetY = (cssHeight - displayHeight) / 2;
  } else {
    // 图像更高，以高度为准
    displayHeight = cssHeight;
    displayWidth = cssHeight * imageAspectRatio;
    offsetX = (cssWidth - displayWidth) / 2;
    offsetY = 0;
  }
  
  // 保存图像显示区域（现在与CSS坐标系统一致）
  imageDisplayArea.value = {
    x: offsetX,
    y: offsetY,
    width: displayWidth,
    height: displayHeight
  };
  
  // 调试信息
  console.log('图像显示区域:', {
    cssSize: { width: cssWidth, height: cssHeight },
    imageSize: { width: img.naturalWidth, height: img.naturalHeight },
    displayArea: imageDisplayArea.value,
    aspectRatios: { canvas: canvasAspectRatio, image: imageAspectRatio }
  });
  
  // 绘制图像到画布
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, offsetX, offsetY, displayWidth, displayHeight);
  
  // 重新绘制所有框
  redrawCanvas();
};

// 触摸事件处理
const handleTouchStart = (event) => {
  event.preventDefault();
  if (event.touches.length === 1) {
    const touch = event.touches[0];
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    startDrawing(mouseEvent);
  }
};

const handleTouchMove = (event) => {
  event.preventDefault();
  if (event.touches.length === 1) {
    const touch = event.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    draw(mouseEvent);
  }
};

const handleTouchEnd = (event) => {
  event.preventDefault();
  const mouseEvent = new MouseEvent('mouseup');
  stopDrawing(mouseEvent);
};

/**
 * 处理文件选择
 */
const handleFileSelect = async (event) => {
  const file = event.target.files[0];
  if (file) {
    await setSelectedFile(file);
  }
};

/**
 * 压缩图像到最大2048px，90%质量JPG格式
 */
const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // 计算新的尺寸，保持宽高比
      let width = img.naturalWidth;
      let height = img.naturalHeight;
      const maxSize = 2048;
      
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      // 创建源canvas
      const sourceCanvas = document.createElement('canvas');
      sourceCanvas.width = img.naturalWidth;
      sourceCanvas.height = img.naturalHeight;
      const sourceCtx = sourceCanvas.getContext('2d');
      sourceCtx.drawImage(img, 0, 0);
      
      // 创建目标canvas
      const destCanvas = document.createElement('canvas');
      destCanvas.width = Math.round(width);
      destCanvas.height = Math.round(height);
      
      // 使用pica进行高质量缩放
      const pica = new Pica();
      pica.resize(sourceCanvas, destCanvas, {
        quality: 3, // 最高质量
        alpha: true
      }).then(() => {
        // 转换为90%质量的JPG
        destCanvas.toBlob((blob) => {
          if (blob) {
            // 创建新的File对象，保持原始文件名但改为jpg扩展名
            const fileName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
            const compressedFile = new File([blob], fileName, { type: 'image/jpeg' });
            resolve(compressedFile);
          } else {
            reject(new Error('图像压缩失败'));
          }
        }, 'image/jpeg', 0.9); // 90%质量
      }).catch(reject);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

/**
 * 设置选中的文件（统一处理函数）
 */
const setSelectedFile = async (file) => {
  if (file && file.type.startsWith('image/')) {
    try {
      // 压缩图像
      const compressedFile = await compressImage(file);
      selectedFile.value = compressedFile;
      
      // 创建预览URL
      const reader = new FileReader();
      reader.onload = (e) => {
        previewUrl.value = e.target.result;
        // 清除之前的框
        boxes.value = [];
        currentBox.value = null;
        // 等待图像加载完成后设置画布
        setTimeout(() => {
          if (imageRef.value) {
            setupCanvas();
          }
        }, 200); // 增加延迟确保图像完全加载
      };
      reader.readAsDataURL(compressedFile);
      
      // 清除错误信息
      error.value = null;
    } catch (err) {
      console.error('图像压缩失败:', err);
      // 如果压缩失败，使用原文件
      selectedFile.value = file;
      
      // 创建预览URL
      const reader = new FileReader();
      reader.onload = (e) => {
        previewUrl.value = e.target.result;
        // 清除之前的框
        boxes.value = [];
        currentBox.value = null;
        // 等待图像加载完成后设置画布
        setTimeout(() => {
          if (imageRef.value) {
            setupCanvas();
          }
        }, 200);
      };
      reader.readAsDataURL(file);
      
      // 清除错误信息
      error.value = null;
    }
  } else {
    const errorMessage = '请选择有效的图片文件';
    error.value = errorMessage;
    
    // 使用Element Plus通知显示错误
    ElNotification({
      title: '文件格式错误',
      message: errorMessage,
      type: 'error',
      duration: 3000,
      position: 'top-right'
    });
  }
};

/**
 * 拖拽事件处理
 */
const handleDragEnter = (e) => {
  e.preventDefault();
  isDragging.value = true;
};

const handleDragOver = (e) => {
  e.preventDefault();
};

const handleDragLeave = (e) => {
  e.preventDefault();
  // 只有当离开的是拖拽区域本身时才设置为false
  if (e.currentTarget.contains(e.relatedTarget)) return;
  isDragging.value = false;
};

const handleDrop = async (e) => {
  e.preventDefault();
  isDragging.value = false;
  
  const files = e.dataTransfer.files;
  if (files.length > 0) {
    await setSelectedFile(files[0]);
  }
};

/**
 * 清除选择的文件
 */
const clearFile = () => {
  selectedFile.value = null;
  previewUrl.value = '';
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

/**
 * 将结果图像重新输入为原始图像
 */
const useResultAsInput = async () => {
  if (generatedImageBlob.value) {
    try {
      // 创建File对象
      const file = new File([generatedImageBlob.value], `generated_${Date.now()}.png`, { type: 'image/png' });
      
      // 设置为新的输入图像（会自动压缩）
      await setSelectedFile(file);
      
      // 清除生成结果
      if (generatedImageUrl.value && generatedImageUrl.value.startsWith('blob:')) {
        URL.revokeObjectURL(generatedImageUrl.value);
      }
      generatedImageUrl.value = '';
      generatedImageBlob.value = null;
      paidImageHash.value = ''; // 清除扣费记录，因为现在是新的图片
      
      // 显示成功通知
      ElNotification({
        title: '设置成功',
        message: '已将生成结果设置为新的输入图像',
        type: 'success',
        duration: 3000,
        position: 'top-right'
      });
      
      error.value = null;
    } catch (err) {
      console.error('设置图片时发生错误:', err);
      const errorMessage = '设置图片失败，请重试';
      error.value = errorMessage;
      
      // 使用Element Plus通知显示错误
      ElNotification({
        title: '设置失败',
        message: errorMessage,
        type: 'error',
        duration: 4000,
        position: 'top-right'
      });
    }
  } else {
    ElNotification({
      title: '设置失败',
      message: '图片数据不可用，请重新生成图片',
      type: 'error',
      duration: 4000,
      position: 'top-right'
    });
  }
};

/**
 * 处理图像到图像转换
 */
const processImage = async () => {
  if (!selectedFile.value) {
    const errorMessage = '请先选择一张图片';
    error.value = errorMessage;
    
    // 使用Element Plus通知显示错误
    ElNotification({
      title: '缺少输入',
      message: errorMessage,
      type: 'warning',
      duration: 3000,
      position: 'top-right'
    });
    return;
  }
  
  if (!userPrompt.value.trim()) {
    const errorMessage = '请输入提示词';
    error.value = errorMessage;
    
    // 使用Element Plus通知显示错误
    ElNotification({
      title: '缺少输入',
      message: errorMessage,
      type: 'warning',
      duration: 3000,
      position: 'top-right'
    });
    return;
  }

  isLoading.value = true;
  error.value = null;
  
  // 清理之前的blob URL
  if (generatedImageUrl.value && generatedImageUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(generatedImageUrl.value);
  }
  
  generatedImageUrl.value = '';
  generatedImageBlob.value = null;
  paidImageHash.value = ''; // 清除已扣费记录
  generationProgress.value = 0;
  progressMessage.value = '';
  isQueued.value = false;
  queuePosition.value = 0;
  
  // 停止之前的队列轮询
  stopQueuePolling();
  
  // 开始倒计时
  startCountdown();
  
  // 模拟进度增长 - 更平滑地增长到99%
  const progressInterval = setInterval(() => {
    if (generationProgress.value < 99) {
      // 16秒内平滑增长到99%
      const increment = 99 / 16; // 每秒增长
      generationProgress.value += increment;
      if (generationProgress.value > 99) generationProgress.value = 99;
    }
  }, 1000); // 每秒更新一次

  try {
    // 如果有画框，将原图分辨率导出
    let imageToSend = selectedFile.value;
    if (boxes.value.length > 0 && imageRef.value) {
      const img = imageRef.value;
      // 创建与原图分辨率一致的离屏canvas
      const offCanvas = document.createElement('canvas');
      offCanvas.width = img.naturalWidth;
      offCanvas.height = img.naturalHeight;
      const offCtx = offCanvas.getContext('2d');
      // 先绘制原图
      offCtx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
      // 绘制所有映射后的框
      boxes.value.forEach(box => {
        const mapped = mapBoxToOriginalImage(box);
        offCtx.save();
        offCtx.strokeStyle = mapped.color;
        offCtx.lineWidth = mapped.lineWidth;
        offCtx.lineCap = 'square';
        offCtx.lineJoin = 'miter';
        offCtx.strokeRect(mapped.x, mapped.y, mapped.width, mapped.height);
        offCtx.restore();
      });
      // 生成blob
      const blob = await new Promise((resolve) => {
        offCanvas.toBlob(resolve, 'image/png');
      });
      imageToSend = new File([blob], selectedFile.value.name, { type: 'image/png' });
    }
    
    // 创建FormData对象
    const formData = new FormData();
    formData.append('image', imageToSend);
    formData.append('prompt', userPrompt.value);

    // 发送请求到后端
    const response = await axios.post(`${API_BASE_URL}/api/i2i`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.data.success) {
      // 直接显示结果
      const originalUrl = `${API_BASE_URL}/api/i2i/image/${response.data.image_filename}`;
      
      // 获取图片blob数据
      try {
        const imageResponse = await fetch(originalUrl);
        generatedImageBlob.value = await imageResponse.blob();
        // 创建blob URL用于展示
        generatedImageUrl.value = URL.createObjectURL(generatedImageBlob.value);
      } catch (err) {
        console.error('获取图片blob失败:', err);
        // 如果获取失败，使用原始URL作为备用
        generatedImageUrl.value = originalUrl;
      }
      
      generationProgress.value = 100;
      console.log('翻译后的提示词:', response.data.translated_prompt);
      
      // 显示处理成功通知
      ElNotification({
        title: '处理成功',
        message: '图片已成功生成，可以下载或继续编辑',
        type: 'success',
        duration: 3000,
        position: 'top-right'
      });
      
      clearInterval(progressInterval);
      isLoading.value = false;
      stopCountdown();
    } else if (response.status === 202 && response.data.status === 'queued') {
      // 任务进入队列
      isQueued.value = true;
      queuePosition.value = response.data.queue_position;
      
      // 启动队列倒计时
      if (response.data.estimated_wait_time) {
        updateQueueCountdown(response.data.estimated_wait_time);
      }
      
      startQueuePolling(response.data.task_id);
      clearInterval(progressInterval);
      isLoading.value = false;
      stopCountdown();
    } else {
      error.value = response.data.error || '处理失败';
      clearInterval(progressInterval);
      isLoading.value = false;
      stopCountdown();
    }

  } catch (err) {
    console.error('Error processing image:', err);
    
    // 检查是否是401未授权错误（未登录）
    if (err.response?.status === 401) {
      const errorMessage = '您需要先登录才能使用此功能，请前往登录页面';
      ElNotification({
        title: '请先登录',
        message: errorMessage,
        type: 'warning',
        duration: 5000,
        position: 'top-right'
      });
      error.value = errorMessage;
    } else {
      // 其他错误
      let errorMessage = '处理图片时发生错误，请检查网络或后端日志。';
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage = err.response.data.error;
      }
      
      ElNotification({
        title: '处理失败',
        message: errorMessage,
        type: 'error',
        duration: 5000,
        position: 'top-right'
      });
      error.value = errorMessage;
    }
    
    clearInterval(progressInterval);
    isLoading.value = false;
    stopCountdown();
  }
};

/**
 * 给图片加水印（右下角"创意无限AI生成"）
 * @param {string} imageUrl 结果图片URL
 * @returns {Promise<Blob>} 带水印的图片Blob
 */
async function addWatermarkToImage(imageBlob) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      // 设置水印样式
      const fontSize = Math.max(Math.floor(canvas.width / 64), 8); // 字号更小更精致
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      const padding = fontSize * 0.4;
      const text = '创意无限AI生成';
      // 先画阴影
      ctx.shadowColor = 'white';
      ctx.shadowBlur = 6;
      ctx.fillText(text, canvas.width - padding, canvas.height - padding);
      // 再画主文字
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(0,0,0,0.38)';
      ctx.fillText(text, canvas.width - padding, canvas.height - padding);
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('水印生成失败'));
      }, 'image/png');
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(imageBlob);
  });
}

/**
 * 下载生成的图片（带扣费逻辑）
 */
const downloadImage = async () => {
  if (!generatedImageUrl.value) return;
  try {
    // 1. 检查是否已经扣费过
    if (paidImageHash.value) {
      console.log('图片已扣费，直接下载');
      
      // 2. 获取原始文件名和扩展名
      let baseName = 'AI修改图像';
      let ext = 'png';
      if (selectedFile.value && selectedFile.value.name) {
        const nameParts = selectedFile.value.name.split('.');
        if (nameParts.length > 1) {
          ext = nameParts.pop();
          baseName = nameParts.join('.');
        } else {
          baseName = selectedFile.value.name;
        }
      }
      // 生成新文件名
      const fileName = `${baseName}_创意无限AI生成.${ext}`;
      // 3. 先加水印
      if (!generatedImageBlob.value) {
        ElNotification({
          title: '下载失败',
          message: '图片数据不可用，请重新生成图片',
          type: 'error',
          duration: 4000,
          position: 'top-right'
        });
        return;
      }
      const blob = await addWatermarkToImage(generatedImageBlob.value);
      const url = URL.createObjectURL(blob);
      // 4. 创建下载链接
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // 显示下载成功通知
      ElNotification({
        title: '下载成功',
        message: '图片已成功下载到您的设备',
        type: 'success',
        duration: 3000,
        position: 'top-right'
      });
      
      console.log('图片下载成功');
      return;
    }
    
    // 2. 如果未扣费，请求扣费接口
    const resp = await axios.post(`${API_BASE_URL}/check_download_quota`, {
      modelType: 'i2i'
    }, { withCredentials: true });
    if (!resp.data.success) {
      // 检查是否是配额不足
      const errorMessage = resp.data.error || '下载配额不足，无法下载';
      const isQuotaError = errorMessage.includes('配额') || errorMessage.includes('quota') || errorMessage.includes('额度');
      
      ElNotification({
        title: isQuotaError ? '配额不足' : '下载失败',
        message: isQuotaError ? '您的下载配额已用完，请升级套餐或等待每日免费额度重置' : errorMessage,
        type: isQuotaError ? 'warning' : 'error',
        duration: 5000,
        position: 'top-right'
      });
      return;
    }
    // 新增：更新本地配额
    if (resp.data.quotaInfo) {
      updateUserQuotaInfo(resp.data.quotaInfo);
    }
    
    // 记录已扣费的图片hash
    paidImageHash.value = 'paid';
    
    // 显示扣费成功通知
    ElNotification({
      title: '扣费成功',
      message: '已扣除1次下载额度，开始下载图片...',
      type: 'success',
      duration: 3000,
      position: 'top-right'
    });
    
    // 2. 获取原始文件名和扩展名
    let baseName = 'AI修改图像';
    let ext = 'png';
    if (selectedFile.value && selectedFile.value.name) {
      const nameParts = selectedFile.value.name.split('.');
      if (nameParts.length > 1) {
        ext = nameParts.pop();
        baseName = nameParts.join('.');
      } else {
        baseName = selectedFile.value.name;
      }
    }
    // 生成新文件名
    const fileName = `${baseName}_创意无限AI生成.${ext}`;
    // 3. 先加水印
    if (!generatedImageBlob.value) {
      ElNotification({
        title: '下载失败',
        message: '图片数据不可用，请重新生成图片',
        type: 'error',
        duration: 4000,
        position: 'top-right'
      });
      return;
    }
    const blob = await addWatermarkToImage(generatedImageBlob.value);
    const url = URL.createObjectURL(blob);
    // 4. 创建下载链接
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    // 显示下载成功通知
    ElNotification({
      title: '下载成功',
      message: '图片已成功下载到您的设备',
      type: 'success',
      duration: 3000,
      position: 'top-right'
    });
    
    console.log('图片下载成功');
  } catch (err) {
    console.error('下载图片时发生错误:', err);
    
    // 检查是否是401未授权错误（未登录）
    if (err.response?.status === 401) {
      ElNotification({
        title: '请先登录',
        message: '您需要先登录才能下载图片，请前往登录页面',
        type: 'warning',
        duration: 5000,
        position: 'top-right'
      });
    } else {
      // 其他错误
      ElNotification({
        title: '下载失败',
        message: err.response?.data?.error || err.message || '下载图片失败，请重试',
        type: 'error',
        duration: 4000,
        position: 'top-right'
      });
    }
  }
};
</script>

<template>
  <div id="ai-image-editor" class="app-container">
    <header class="app-header">
      <h1 class="app-title">AI 图像编辑器</h1>
      <p class="app-subtitle">智能图像重新生成 · 专业级AI工具</p>
    </header>

    <main class="main-workspace">

      
      <!-- 左侧：输入区域 -->
      <section class="input-panel">
        <h2 class="panel-title">输入设置</h2>
        
        <!-- 图片上传区域 -->
        <div class="upload-section">
          <label class="section-label">原始图像</label>
          <input 
            ref="fileInput"
            type="file" 
            accept="image/*" 
            @change="handleFileSelect"
            :disabled="isLoading"
            style="display: none"
          />
          
          <div 
            v-if="!previewUrl" 
            class="upload-area"
            :class="{ 'drag-over': isDragging }"
            @click="fileInput.click()"
            @dragenter="handleDragEnter"
            @dragover="handleDragOver"
            @dragleave="handleDragLeave"
            @drop="handleDrop"
          >
            <div class="upload-icon">📸</div>
            <div class="upload-text">{{ isDragging ? '松开鼠标上传' : '点击或拖拽图片上传' }}</div>
            <div class="upload-hint">支持 JPG、PNG、WebP 格式</div>
          </div>
          
          <div v-else class="preview-container">
            <!-- 隐藏的原始图像用于画布 -->
            <img 
              ref="imageRef"
              :src="previewUrl" 
              alt="原始图片" 
              style="display: none;"
              @load="setupCanvas"
            />
            
            <!-- 画布用于显示图像和框 -->
            <canvas 
              ref="canvasRef"
              class="preview-canvas"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @mouseleave="stopDrawing"
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            ></canvas>
            
            <button @click="clearFile" class="remove-btn" :disabled="isLoading">
              <span>✕</span>
            </button>
            
            <!-- 框信息显示 -->
            <div v-if="boxes.length > 0" class="boxes-info">
              <span>已绘制 {{ boxes.length }} 个框</span>
            </div>
            
            <!-- 移除画框提示和图像区域提示，将它们移动到画框工具中 -->

          </div>
          
          <!-- 画框工具 - 移到canvas外部 -->
          <div v-if="previewUrl" class="drawing-tools-external">
            <div class="tools-header">
              <span class="tools-title">画框工具</span>
              <button 
                @click="toggleDrawingMode" 
                :class="['drawing-toggle', { active: drawingMode }]"
                :disabled="isLoading"
                title="切换画框模式"
              >
                <span>🖊️</span>
              </button>
            </div>
            
            <!-- 绘制提示 - 移动到工具中 -->
            <div v-if="drawingMode" class="drawing-hints">
              <div v-if="boxes.length === 0" class="drawing-hint-tool">
                <span>💡 拖拽绘制框来标记主体位置</span>
              </div>
              <div class="image-area-hint-tool">
                <span>⚠️ 只在图像区域内绘制</span>
              </div>
            </div>
            
            <div v-if="drawingMode" class="drawing-controls-external">
              <div class="control-row">
                <div class="color-picker">
                  <label>颜色:</label>
                  <input 
                    type="color" 
                    v-model="selectedColor" 
                    :disabled="isLoading"
                    class="color-input"
                  />
                </div>
                
                <div class="line-width">
                  <label>线宽:</label>
                  <input 
                    type="range" 
                    v-model="lineWidth" 
                    min="1" 
                    max="10" 
                    :disabled="isLoading"
                    class="width-slider"
                  />
                  <span class="width-value">{{ lineWidth }}px</span>
                </div>
              </div>
              
              <div class="box-actions">
                <button @click="removeLastBox" :disabled="isLoading || boxes.length === 0" class="action-btn">
                  <span>↶</span>
                  <span class="btn-text">撤销</span>
                </button>
                <button @click="clearBoxes" :disabled="isLoading || boxes.length === 0" class="action-btn">
                  <span>🗑️</span>
                  <span class="btn-text">清空</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 提示词输入 -->
        <div class="prompt-section">
          <label class="section-label" for="prompt">编辑指令</label>
          <textarea
            id="prompt"
            v-model="userPrompt"
            :disabled="isLoading"
            placeholder="请描述您希望如何修改这张图片..."
            class="prompt-input"
            rows="4"
          ></textarea>
        </div>

        <!-- 生成按钮 -->
        <button 
          @click="processImage" 
          :disabled="isLoading || !selectedFile || !userPrompt.trim()"
          class="generate-button"
        >
          <span v-if="isLoading" class="loading-content">
            <div class="spinner"></div>
            <span v-if="isCountdownActive && countdown > 0">
              处理中 ({{ countdown }}s)
            </span>
            <span v-else-if="isLoading">
              请等待...
            </span>
          </span>
          <span v-else>开始生成</span>
        </button>
      </section>

      <!-- 右侧：输出区域 -->
      <section class="output-panel">
        <h2 class="panel-title">生成结果</h2>
        
        <!-- 教程组件 -->
        <div v-if="showTutorial" class="tutorial-overlay">
          <TutorialSection />
        </div>
        
        <div v-if="generatedImageUrl" class="result-section">
          <div class="result-image-wrapper">
            <img :src="generatedImageUrl" alt="生成的图片" class="result-image" />
          </div>
          <div class="result-actions">
            <button @click="downloadImage" class="download-button">
              <span class="download-icon">⬇</span>
              下载图片
            </button>
            <button @click="useResultAsInput" class="reuse-button">
              <span class="reuse-icon">🔄</span>
              重新编辑
            </button>
          </div>
        </div>
        
        <div v-else-if="isLoading" class="loading-section">
          <div class="loading-animation">
            <div class="loading-circle"></div>
            <div class="loading-circle"></div>
            <div class="loading-circle"></div>
          </div>
          
          <!-- 进度条 -->
          <div class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: generationProgress + '%' }"></div>
            </div>
          </div>
          
          <p class="loading-text">
            <span v-if="progressMessage">
              {{ progressMessage }}
            </span>
            <span v-else-if="isCountdownActive && countdown > 0">
              请勿退出或刷新，AI正在处理您的图片，预计还需 {{ countdown }} 秒...
            </span>
            <span v-else>
              AI正在处理您的图片，请稍候...
            </span>
          </p>
        </div>
        
        <div v-else-if="isQueued" class="queue-section">
          <div class="queue-animation">
            <div class="queue-icon">⏳</div>
          </div>
          
          <h3 class="queue-title">任务已加入队列</h3>
          <p class="queue-text">
            当前排队位置：第 {{ queuePosition }} 位<br>
            预估等待时间：{{ queuePosition * 16 }} 秒（每个任务约16秒）<br>
            系统将自动处理您的任务，请耐心等待...
          </p>
          
          <!-- 队列倒计时显示 -->
          <div v-if="isCountdownActive && countdown > 0" class="queue-countdown">
            <div class="countdown-text">
              剩余等待时间：{{ countdown }} 秒<br>
              <span class="countdown-hint">（基于每个任务16秒处理时间预估）</span>
            </div>
            <div class="countdown-progress">
              <div class="countdown-bar">
                <div class="countdown-fill" :style="{ width: (countdown / (queuePosition * 16)) * 100 + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-section">
          <div class="empty-icon">🎨</div>
          <p class="empty-text">处理完成的图片将在此显示</p>
        </div>
      </section>
    </main>

    <!-- 错误提示 -->
    <div v-if="error" class="error-notification">
      <div class="error-content">
        <span class="error-icon">⚠</span>
        <span class="error-text">{{ error }}</span>
        <button @click="error = null" class="error-close">✕</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 字体优化 - 确保中文清晰显示 */
* {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/* 主容器 */
.app-container {
  min-height: 100vh;
  background-color: #fafbfc;
  padding: 24px;
  box-sizing: border-box;
  position: relative;
}

/* 头部 - 改进样式 */
.app-header {
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  z-index: 1;
}

.app-title {
  font-size: 36px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  letter-spacing: -1px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.app-subtitle {
  font-size: 18px;
  color: #666;
  margin: 0;
  font-weight: 400;
}

/* 主工作区 */
.main-workspace {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  position: relative;
  z-index: 1;
}

/* 面板通用样式 - 改进阴影和边框 */
.input-panel,
.output-panel {
  background: #ffffff;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e5e5;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.input-panel:hover,
.output-panel:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 12px 24px rgba(0, 0, 0, 0.08);
}

.panel-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 28px 0;
  padding-bottom: 16px;
  border-bottom: 3px solid #f0f0f0;
  position: relative;
}

.panel-title::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 60px;
  height: 3px;
  background: #667eea;
  border-radius: 2px;
}

/* 输入面板 */
.upload-section,
.prompt-section {
  margin-bottom: 28px;
}

.section-label {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 上传区域 - 改进样式 */
.upload-area {
  border: 3px dashed #cbd5e0;
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  position: relative;
  overflow: hidden;
}

.upload-area::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  transition: left 0.5s;
}

.upload-area:hover::before {
  left: 100%;
}

.upload-area:hover {
  border-color: #667eea;
  background: linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
}

/* 拖拽状态样式 */
.upload-area.drag-over {
  border-color: #667eea;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
  transform: scale(1.02) translateY(-4px);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.25);
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 16px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.upload-text {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
}

.upload-hint {
  font-size: 14px;
  color: #718096;
  font-weight: 500;
}

/* 预览容器 - 改进样式 */
.preview-container {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  min-width: 400px;
  transition: all 0.3s ease;
}

.preview-container:hover {
  transform: translateY(-4px);
  box-shadow: 
    0 15px 40px rgba(0, 0, 0, 0.2),
    0 6px 16px rgba(0, 0, 0, 0.15);
}

.preview-image {
  width: 100%;
  height: auto;
  max-height: 400px;
  object-fit: contain;
  display: block;
  background-color: #f8f9fa;
  border-radius: 12px;
}

/* 画布样式 */
.preview-canvas {
  width: 100%;
  height: auto;
  max-height: 400px;
  min-width: 400px;
  min-height: 400px;
  display: block;
  background-color: #f8f9fa;
  border-radius: 12px;
  cursor: crosshair;
  transition: all 0.3s ease;
  touch-action: none; /* 防止触摸滚动 */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

.preview-canvas:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 外部画框工具样式 */
.drawing-tools-external {
  margin-top: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.tools-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.tools-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.drawing-controls-external {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.control-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

/* 画框工具样式 */
.drawing-tools {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 10;
}

.drawing-toggle {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  color: #4a5568;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.drawing-toggle:hover:not(:disabled) {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.drawing-toggle.active {
  background: #667eea;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.drawing-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.drawing-controls {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 160px;
}

.color-picker,
.line-width {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4a5568;
}

.color-picker label,
.line-width label {
  font-weight: 600;
  min-width: 40px;
}

.color-input {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: none;
}

.width-slider {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #e2e8f0;
  outline: none;
  cursor: pointer;
}

.width-slider::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.width-value {
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  min-width: 30px;
  text-align: center;
}

.box-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: #f7fafc;
  color: #4a5568;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
}

.action-btn:hover:not(:disabled) {
  background: #e2e8f0;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-text {
  font-size: 12px;
  font-weight: 500;
}

.boxes-info {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(10px);
}





.remove-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #e53e3e, #c53030);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(229, 62, 62, 0.3);
}

.remove-btn:hover {
  background: linear-gradient(135deg, #c53030, #a52a2a);
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(229, 62, 62, 0.4);
}

/* 提示词输入 - 改进样式 */
.prompt-input {
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  line-height: 1.6;
  resize: vertical;
  transition: all 0.3s ease;
  box-sizing: border-box;
  background: linear-gradient(135deg, #ffffff 0%, #f7fafc 100%);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.prompt-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 
    0 0 0 4px rgba(102, 126, 234, 0.1),
    inset 0 2px 4px rgba(0, 0, 0, 0.05);
  background: #ffffff;
  transform: translateY(-1px);
}

.prompt-input::placeholder {
  color: #a0aec0;
  font-style: italic;
}

/* 生成按钮 - 改进样式 */
.generate-button {
  width: 100%;
  padding: 18px 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 
    0 8px 25px rgba(102, 126, 234, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.generate-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s;
}

.generate-button:hover:not(:disabled)::before {
  left: 100%;
}

.generate-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 
    0 12px 35px rgba(102, 126, 234, 0.4),
    0 6px 16px rgba(0, 0, 0, 0.15);
}

.generate-button:active:not(:disabled) {
  transform: translateY(0);
}

.generate-button:disabled {
  background: linear-gradient(135deg, #a0aec0 0%, #718096 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.loading-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 输出面板 */
.result-section {
  text-align: center;
}

.result-image-wrapper {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: #ffffff;
  margin-bottom: 24px;
}

.result-image {
  width: 100%;
  height: auto;
  max-height: 600px;
  object-fit: contain;
  display: block;
  background-color: #ffffff;
}

.result-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.download-button {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(72, 187, 120, 0.2);
}

.download-button:hover {
  background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
}

.download-icon {
  font-size: 16px;
}

/* 重新编辑按钮 */
.reuse-button {
  background: linear-gradient(135deg, #9f7aea 0%, #805ad5 100%);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(159, 122, 234, 0.2);
}

.reuse-button:hover {
  background: linear-gradient(135deg, #805ad5 0%, #6b46c1 100%);
  box-shadow: 0 4px 12px rgba(159, 122, 234, 0.3);
}

.reuse-icon {
  font-size: 16px;
}

/* 加载状态 - 改进动画 */
.loading-section {
  text-align: center;
  padding: 80px 20px;
}

.loading-animation {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
}

.loading-circle {
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.loading-circle:nth-child(1) { animation-delay: -0.32s; }
.loading-circle:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-text {
  font-size: 16px;
  color: #4a5568;
  margin: 0;
  font-weight: 500;
  line-height: 1.6;
}

/* 进度条样式 */
.progress-container {
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.progress-bar {
  width: 100%;
  max-width: 300px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.5s ease;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}



/* 队列状态样式 */
.queue-section {
  text-align: center;
  padding: 60px 20px;
  color: #4a5568;
}

.queue-animation {
  margin-bottom: 24px;
}

.queue-icon {
  font-size: 48px;
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.queue-title {
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 16px 0;
}

.queue-text {
  font-size: 16px;
  line-height: 1.6;
  color: #718096;
  margin: 0 0 20px 0;
}

/* 队列倒计时样式 */
.queue-countdown {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.countdown-text {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
}

.countdown-hint {
  font-size: 12px;
  font-weight: 400;
  color: #718096;
  font-style: italic;
}

.countdown-progress {
  display: flex;
  justify-content: center;
}

.countdown-bar {
  width: 100%;
  max-width: 200px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.countdown-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.5s ease;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

/* 空状态 - 改进样式 */
.empty-section {
  text-align: center;
  padding: 80px 20px;
  color: #718096;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.7;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.empty-text {
  font-size: 16px;
  margin: 0;
  font-weight: 500;
}

/* 错误通知 - 改进样式 */
.error-notification {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
  max-width: 450px;
  animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideIn {
  from {
    transform: translateX(100%) scale(0.9);
    opacity: 0;
  }
  to {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
}

.error-content {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.1),
    0 4px 12px rgba(0, 0, 0, 0.05);
}

.error-icon {
  color: #e53e3e;
  font-size: 20px;
  flex-shrink: 0;
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
}

.error-text {
  color: #c53030;
  font-size: 15px;
  flex: 1;
  font-weight: 600;
  line-height: 1.5;
}

.error-close {
  background: rgba(245, 101, 101, 0.2);
  border: none;
  color: #e53e3e;
  cursor: pointer;
  font-size: 18px;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.3s ease;
  font-weight: bold;
}

.error-close:hover {
  background: rgba(245, 101, 101, 0.3);
  color: #c53030;
  transform: scale(1.1);
}

/* 响应式设计 - 改进 */
@media (max-width: 768px) {
  .main-workspace {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  
  .app-container {
    padding: 16px;
  }
  
  .app-title {
    font-size: 28px;
  }
  
  .input-panel,
  .output-panel {
    padding: 24px;
  }
  
  .error-notification {
    left: 16px;
    right: 16px;
    max-width: none;
  }
  
  .result-actions {
    flex-direction: column;
    gap: 12px;
  }
  
  .upload-area {
    padding: 32px 16px;
  }
  
  .upload-icon {
    font-size: 36px;
  }
  
  /* 移动端画框工具调整 */
  .drawing-tools {
    top: 8px;
    right: 8px;
  }
  
  .drawing-controls {
    min-width: 140px;
    padding: 8px;
  }
  
  .drawing-controls-external {
    gap: 8px;
  }
  
  .control-row {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
  
  .color-picker,
  .line-width {
    font-size: 11px;
  }
  
  .action-btn {
    padding: 6px 8px;
    font-size: 12px;
  }
  
  .btn-text {
    font-size: 11px;
  }
  
  .boxes-info {
    bottom: 8px;
    right: 8px;
    font-size: 11px;
    padding: 4px 8px;
  }
  
  /* 移动端绘制提示样式调整 */
  .drawing-hints {
    margin-bottom: 8px;
    gap: 6px;
  }
  
  .drawing-hint-tool,
  .image-area-hint-tool {
    padding: 6px 10px;
    font-size: 11px;
  }
}

/* 添加滚动条美化 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 移除旧的绘制提示样式，替换为工具内的提示样式 */
.drawing-hints {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.drawing-hint-tool {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.image-area-hint-tool {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #1a202c;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.2);
}
</style>
